"use client"

import { useEffect, useState } from "react"
import { ref as storageRef, listAll, getDownloadURL } from "firebase/storage"
import { storage } from "../Firebase/firebase-config"
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId"
import { getDatabase, ref as dbRef, push, set } from "firebase/database"
import { FaDownload, FaExternalLinkAlt, FaFileAlt } from "react-icons/fa"
import "./Downloads.css"
import { IoEyeSharp } from "react-icons/io5"
import { MdInfo } from "react-icons/md"

const db = getDatabase()

const getIndianTimestamp = () => {
  const now = new Date()
  const istOffset = 5.5 * 60 * 60 * 1000
  const istTime = new Date(now.getTime() + istOffset)
  return istTime.toISOString().replace("T", " ").replace("Z", "")
}

const toOrdinal = (num) => {
  const n = parseInt(num, 10)
  if (isNaN(n)) return num

  if (n % 100 >= 11 && n % 100 <= 13) {
    return n + "th"
  }

  switch (n % 10) {
    case 1: return n + "st"
    case 2: return n + "nd"
    case 3: return n + "rd"
    default: return n + "th"
  }
}

const isValidWeek = (num) => num >= 1 && num <= 52

const formatDetails = (type, input) => {
  let trimmed = input.trim()
  if (!trimmed) return trimmed

  // If user typed letters (Q1, 2nd, etc.), respect and return as-is
  if (/[a-zA-Z]/.test(trimmed)) {
    return trimmed
  }

  const year = new Date().getFullYear()

  // Normalize spaces
  trimmed = trimmed.replace(/\s+/g, "")

  const segments = trimmed.split(",")

  const results = []

  for (let seg of segments) {
    // Handle range: 1-8
    if (seg.includes("-")) {
      const [startRaw, endRaw] = seg.split("-")
      const start = parseInt(startRaw, 10)
      const end = parseInt(endRaw, 10)

      if (isNaN(start) || isNaN(end)) return input

      if (!isValidWeek(start) || !isValidWeek(end)) {
        alert("Week number must be between 1 and 52")
        return input
      }

      if (start > end) {
        alert("Invalid range: start week cannot be greater than end week")
        return input
      }

      results.push(`${toOrdinal(start)}–${toOrdinal(end)}`)
    }
    // Handle single number
    else {
      const num = parseInt(seg, 10)
      if (isNaN(num)) return input

      if (!isValidWeek(num)) {
        alert("Week number must be between 1 and 52")
        return input
      }

      results.push(toOrdinal(num))
    }
  }

  // Build final string
  if (type === "mtc") {
    if (results.length === 1) {
      return `Week ${results[0]}`
    }
    return `Week ${results.slice(0, -1).join(", ")} & ${results.slice(-1)}`
  }

  // For statements / quarters
  if (results.length === 1) {
    return `${results[0]} Quarter ${year}`
  }

  return `${results.slice(0, -1).join(", ")} & ${results.slice(-1)} Quarter ${year}`
}


const sendRequestNotification = async (effectiveUserId, companyName, category, rawDetails) => {
  const formattedDetails = formatDetails(category.toLowerCase(), rawDetails)
  const notificationRef = dbRef(db, `supplier_notifications/Docs_Request/${effectiveUserId}`)
  const newNotifRef = push(notificationRef)

  const message = category === "MTC"
    ? `${companyName} has requested MTC for ${formattedDetails}.`
    : `${companyName} has requested Statement for ${formattedDetails}.`

  const newNotification = {
    category: "Document Request",
    title: "New Document Request",
    message,
    details: formattedDetails,
    gifUrl: "/Images/New notification.gif",
    timestamp: getIndianTimestamp(),
    seen: false,
  }

  try {
    await set(newNotifRef, newNotification)
  } catch (error) {
    console.error("Failed to send notification:", error)
  }
}

const Downloads = ({ category = "mtc", onClose, forcedUserId, companyName = "User" }) => {
  const { userId: hookUserId, loading: idLoading } = useGetCurrentUserId()
  const effectiveUserId = forcedUserId || hookUserId
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const [weekInput, setWeekInput] = useState("")
  const [quarterInput, setQuarterInput] = useState("")
  const [requestSent, setRequestSent] = useState(false)

  useEffect(() => {
    const fetchFiles = async () => {
      if (!effectiveUserId && category !== "formats") return
      if (idLoading) return

      setLoading(true)
      setError("")
      setFiles([])

      try {
        let storagePath = ""
        if (category === "mtc") storagePath = `mtc/${effectiveUserId}`
        else if (category === "statements") storagePath = `user-statements/${effectiveUserId}`
        else if (category === "formats") storagePath = `formats/${effectiveUserId}`

        const listRef = storageRef(storage, storagePath)
        const listResult = await listAll(listRef)

        if (listResult.items.length === 0) {
          setError("No files available.")
          return
        }

        const filePromises = listResult.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef)
          return { name: itemRef.name, url }
        })

        const fileList = await Promise.all(filePromises)
        setFiles(fileList.sort((a, b) => a.name.localeCompare(b.name)))
      } catch (err) {
        console.error("Storage error:", err)
        setError("No files found or access denied.")
      } finally {
        setLoading(false)
      }
    }

    fetchFiles()
  }, [effectiveUserId, category, idLoading])

  const handleDownload = (url, name) => {
    const link = document.createElement("a")
    link.href = url
    link.download = name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleRequest = async () => {
    const rawInput = category === "mtc" ? weekInput : quarterInput
    const details = rawInput.trim()
    if (!details) {
      alert(`Please enter ${category === "mtc" ? "week" : "quarter"} number`)
      return
    }

    await sendRequestNotification(effectiveUserId, companyName, category.toUpperCase(), details)

    setRequestSent(true)
    category === "mtc" ? setWeekInput("") : setQuarterInput("")

    setTimeout(() => setRequestSent(false), 5000)
  }

  const getTitle = () => {
    switch (category) {
      case "mtc": return "MTC Files"
      case "statements": return "Statements"
      case "formats": return "Formats"
      default: return "Downloads"
    }
  }

  const isRequestSection = category === "mtc" || category === "statements"
  const currentInput = category === "mtc" ? weekInput : quarterInput
  const setCurrentInput = category === "mtc" ? setWeekInput : setQuarterInput

  const getInfoContent = () => {
    switch (category) {
      case "mtc":
        return (
          <>
            <h3>How to get your MTC</h3>
            <ul>
              <li>Enter week numbers (1–52) in the box above</li>
              <li>You can enter multiple weeks like: <strong>5,8,12-15,22</strong></li>
              <li>Click <strong>Request</strong></li>
              <li>You will receive notification when MTC uploaded</li>
              <li>Once uploaded, files appear here automatically</li>
              <li>You can then <strong>View</strong> or <strong>Download</strong> them</li>
            </ul>
            <p><small>Note: Make sure week numbers are correct.</small></p>
          </>
        );

      case "statements":
        return (
          <>
            <h3>How to get your Statements</h3>
            <ul>
              <li>Enter quarter numbers or date range in the box</li>
              <li>Examples that work:</li>
              <li style={{ marginLeft: "1.5rem" }}>• <code>1-12-2025 to 31-12-2025</code></li>
              <li style={{ marginLeft: "1.5rem" }}>• <code>Q1, 2026</code></li>
              <li>Click <strong>Request</strong></li>
              <li>You will receive notification when Statements uploaded</li>
              <li>Once uploaded, files appear here automatically</li>
              <li>You can then <strong>View</strong> or <strong>Download</strong> them</li>
            </ul>
            <p><small>Note: Make sure quarter numbers or date range are correct.</small></p>
          </>
        );

      case "formats":
        return (
          <>
            <h3>About Document Formats</h3>
            <ul>
              <li>These are official document formats provided by us</li>
              <li>Use these formats for Purchase Order on your Letter Head</li>
              <li>Download the required format and fill it as instructed</li>
            </ul>
          </>
        );

      default:
        return <p>No information available for this section.</p>;
    }
  };

  return (
    <>
      <div className="file-popup-overlay" onClick={onClose} />

      <div className="dfile-popup">
        {/* Header with Title */}
        <div className="file-popup-header">
          <div className="header-center">
            <h2>{getTitle()}</h2>
            <button
              className="D-info"
              onClick={() => setShowInfoModal(true)}
              title="How it works"
              aria-label="Show information"
            >
              <MdInfo />
            </button>
          </div>
          <button className="dclose-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* NO TABS — Only selected content */}
        <div className="file-popup-content">
          {/* Request Form (only for MTC & Statements) */}
          {isRequestSection && (
            <div className="request-section">
              <label className="input-label">
                Enter {category === "mtc" ? "Week" : "Quarter"} Number
              </label>
              <div className="input-group">
                <input
                  type="text"
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  placeholder={
                    category === "mtc"
                      ? "Enter 1-52 Weeks"
                      : "e.g: 1-12-2025 to 31-12-2025"
                  }

                  className="request-input"
                />
                <button className="dbtn-request" onClick={handleRequest}>
                  Request
                </button>
              </div>
              {requestSent && (
                <p className="success-msg text-center bg-success text-white pt-1 pb-1">✓ Request sent successfully!</p>
              )}
            </div>
          )}

          {/* File List */}
          {loading ? (
            <p className="status-text">Loading files...</p>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : files.length === 0 ? (
            <p className="status-text">
              {category === "formats"
                ? "No document templates available."
                : "No files available yet."}
            </p>
          ) : (
            <div className="file-grid">
              {files.map((file, idx) => (
                <div key={idx} className="file-card">
                  <div className="file-icon">
                    <FaFileAlt />
                  </div>
                  <div className="file-name" title={file.name}>
                    {file.name}
                  </div>
                  <div className="file-actions">
                    <button
                      className="dbtn-view"
                      onClick={() => window.open(file.url, "_blank")}
                      title="View"
                    >
                      <IoEyeSharp />
                    </button>
                    <button
                      className="dbtn-down"
                      onClick={() => handleDownload(file.url, file.name)}
                      title="Download"
                    >
                      <FaDownload />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="file-popup-footer">
          <button className="btn-outline" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      {showInfoModal && (
        <>
          <div
            className="info-modal-overlay"
            onClick={() => setShowInfoModal(false)}
          />
          <div className="info-modal">
            <div className="info-modal-header">
              <h3>Information</h3>
              <button
                className="info-close-btn"
                onClick={() => setShowInfoModal(false)}
              >
                ×
              </button>
            </div>
            <div className="info-modal-body">
              {getInfoContent()}
            </div>
            <div className="info-modal-footer">
              <button
                className="btn-outline"
                onClick={() => setShowInfoModal(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default Downloads